import handleServerSideEvents from './handleServerSideEvents.ts';
import { StreamingUpdateData } from './responses-types.ts';

const isTerminalSignal = (line: string): boolean =>
    line === 'event: done' ||
    line === 'data: [DONE]' ||
    line === 'event: error' ||
    line === 'data: [ERROR]';

/**
 * Stateful processor for SSE lines.
 *
 * Pages' SSE wire format is one event per `event:` line plus one or more
 * `data:` lines, terminated by a blank line. The processor buffers the most
 * recent `event:`/`data:` line and flushes it to `handleServerSideEvents` when
 * the event terminator (blank line) or a terminal signal arrives.
 *
 * Lines that don't start with `event:` or `data:` are ignored — they're either
 * fragments left over from chunk-boundary splits in the transport layer or
 * unsupported continuation syntax.
 */
const createSseLineProcessor = (
    onEvent: (update: StreamingUpdateData) => void
): ((line: string) => void) => {
    let buffer = '';

    const flush = (): void => {
        if (buffer) {
            handleServerSideEvents(buffer, onEvent);
            buffer = '';
        }
    };

    return (line: string): void => {
        if (line === '') {
            flush();
            return;
        }

        if (!line.startsWith('event:') && !line.startsWith('data:')) {
            return;
        }

        if (isTerminalSignal(line)) {
            flush();
            handleServerSideEvents(line, onEvent);
            return;
        }

        buffer = line;
    };
};

export default createSseLineProcessor;
